import { TabletButton } from '@campfire/tablet-button';
import { Box, Switch, TextField, Typography } from '@material-ui/core';
import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { useCampfireFetch } from '../../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../../global/config/useApiUrl';
import { useSnackbar } from '../../../../../global/config/useSnackbar';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';
import { GET_ADMIN_CONSOLE_OPS_DEFAULT_ROSTER_START_DATE } from './admin-console-operations.gql';
import { GetAdminConsoleOpsDefaultRosterStartDate } from './__generated__/GetAdminConsoleOpsDefaultRosterStartDate';

export function DefaultRosterStartDate() {
  const { setSnackbar } = useSnackbar();
  const apiUrl = useApiUrl();
  const { theme } = useCampfireTheme();
  const campfireMutation = useCampfireFetch({ defer: true });

  const { data, refetch } = useCampfireQuery<GetAdminConsoleOpsDefaultRosterStartDate, undefined>(
    GET_ADMIN_CONSOLE_OPS_DEFAULT_ROSTER_START_DATE
  );

  const [enabled, setEnabled] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    const serverValue = data?.tm.tenant?.config?.defaultRosterStartDate;
    if (serverValue) {
      setEnabled(true);
      setStartDate(DateTime.fromISO(serverValue).toISODate());
    } else {
      setEnabled(false);
      setStartDate('');
    }
    setHasChanges(false);
  }, [data]);

  const handleSave = () => {
    campfireMutation
      .run({
        url: `${apiUrl}/tm/config/default-roster-start-date`,
        method: 'post',
        data: {
          startDate: enabled && startDate !== '' ? startDate : null,
        },
      })
      .then(() => {
        if (refetch) {
          setTimeout(refetch, 0);
        }
        setSnackbar({
          variant: 'success',
          open: true,
          message: 'Roster start date updated',
        });
        setHasChanges(false);
      })
      .catch(() => {
        setSnackbar({
          variant: 'error',
          open: true,
          message: 'Failed to update roster start date',
        });
      });
  };

  const isPast = startDate !== '' && DateTime.fromISO(startDate) < DateTime.local().startOf('day');
  const canSave = hasChanges && (!enabled || startDate !== '');

  return (
    <Box mt={2}>
      <Box display='flex' alignItems='center'>
        <Switch
          checked={enabled}
          onChange={() => {
            setEnabled(!enabled);
            if (enabled) {
              setStartDate('');
            }
            setHasChanges(true);
          }}
          color='primary'
          inputProps={{ 'aria-label': 'enable default roster start date' }}
        />
        <Box ml={1}>
          <Typography style={{ fontWeight: 500, fontSize: '14px', display: 'flex', alignItems: 'center' }}>
            Set Roster Start Date
          </Typography>
          <Typography variant='body2' style={{ fontSize: '13px', color: '#808080' }}>
            Volunteer roster views open from a fixed date instead of today
          </Typography>
        </Box>
      </Box>

      {enabled && (
        <Box mt={3} ml={7}>
          <Typography style={{ fontWeight: 600, fontSize: '14px', marginBottom: '8px' }}>Start Date</Typography>
          <Typography variant='body2' style={{ fontSize: '13px', color: '#808080', marginBottom: '16px' }}>
            My Elements and My Rosters will open from this date. The date range shown still follows your existing window
            length, so this only changes where it begins. Volunteers can still change the range themselves.
          </Typography>
          <TextField
            type='date'
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              setHasChanges(true);
            }}
            variant='outlined'
            size='small'
            style={{ width: 200 }}
            InputLabelProps={{ shrink: true }}
          />
          {isPast && (
            <Typography variant='body2' style={{ fontSize: '12px', color: '#808080', marginTop: '8px' }}>
              This date is in the past, so rosters will open on a past window until you update it.
            </Typography>
          )}
        </Box>
      )}

      {hasChanges && (
        <Box display='flex' justifyContent='flex-end' mt={3}>
          <TabletButton
            variant='contained'
            color='primary'
            onClick={handleSave}
            disabled={!canSave}
            style={{
              backgroundColor: theme.color.secondary.main900,
              color: '#ffffff',
              boxSizing: 'border-box',
              borderRadius: '4px',
              textTransform: 'none',
              padding: '7px 12px',
              minWidth: '40px',
            }}
          >
            Save
          </TabletButton>
        </Box>
      )}
    </Box>
  );
}
